/**
 * Copyright 2022 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import * as React from 'react';
import { LoadingHOC, Modal } from '../../../commonComponents';
import { APIContext } from '../../../commonComponents/auth/APIContext';
import { getCustomerServiceRolePolicy } from '../../../graphql/getCustomerServiceRolePolicy';
import { downloadFile } from '../../../utils/download';
import { GraphQLError } from '../../../utils/error';
import { showErrorMessage } from '../../../utils/notifications';

type ServiceRoleReducerState = { template: string | null; isLoading: boolean };
type ServiceRoleReducer = (
  prevState: ServiceRoleReducerState,
  newState: Partial<ServiceRoleReducerState>,
) => ServiceRoleReducerState;

export function GetIAMServiceRoleButton() {
  const { requester } = React.useContext(APIContext);

  const [showModal, setShowModal] = React.useState(false);
  const [{ template, isLoading }, setFetchServiceRoleState] = React.useReducer<ServiceRoleReducer>(
    (prevState, newState) => ({ ...prevState, ...newState }),
    { template: null, isLoading: false },
  );

  const toggleShowModal = () => setShowModal(value => !value);

  async function fetchServiceRoleTemplate() {
    const customer_id = localStorage.getItem('currentCustomer');

    setFetchServiceRoleState({ isLoading: true });
    try {
      const response = await requester({ query: getCustomerServiceRolePolicy, input: { customer_id } });
      setFetchServiceRoleState({ isLoading: false, template: response.data.customerServiceRolePolicy });
    } catch (error) {
      setFetchServiceRoleState({ isLoading: false });
      if (error instanceof GraphQLError) {
        error?.errors?.forEach(err => showErrorMessage(err?.message));
      }
    }
  }

  function handleOpenModal() {
    if (!template) {
      fetchServiceRoleTemplate();
    }
    setShowModal(true);
  }

  function downloadTemplate() {
    if (template) {
      downloadFile(template, 'autopatcher_ssm_service_role.yml');
    }
  }

  return (
    <>
      <div className="box-buttons" onClick={handleOpenModal}>
        <button className="button is-primary">Get IAM Service Role</button>
      </div>
      <Modal
        title="IAM Service Role for AutoPatcher"
        visible={showModal}
        size="regular"
        confirmLabel="Download template"
        confirmAction={downloadTemplate}
        cancelAction={toggleShowModal}
      >
        <LoadingHOC loading={isLoading}>
          <div className="terminal-container">
            {template && (
              <textarea className="terminal-content textarea" value={template} readOnly />
            )}
          </div>
        </LoadingHOC>
      </Modal>
    </>
  );
}
